import moment from "moment";
import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../../components/TableFooter.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import {
  selectBroadcastsSortedByUpdatedAt,
  selectFolders,
  selectIsLoadingBroadcasts,
  selectIsLoadingFolders,
} from "../../selectors.js";
import { fetchBroadcasts } from "../../thunks.js";

import SearchBroadcastForm from "./SearchBroadcastForm.jsx";

const messages = defineMessages({
  noFolder: {
    id: "modules.ArriVideo.components.Broadcasts.noFolder",
    description: "No folder label.",
    defaultMessage: "No folder",
  },
  to: {
    id: "modules.ArriVideo.components.BroadcastForm.to",
    description: "To message",
    defaultMessage: "To",
  },
  action: {
    id: "modules.ArriVideo.components.BroadcastForm.action",
    description: "Action label",
    defaultMessage: "Action",
  },
  folder: {
    id: "modules.ArriVideo.components.BroadcastForm.folder",
    description: "Folder label",
    defaultMessage: "Folder",
  },
});

function Broadcasts() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [error, setError] = useState(undefined);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchTitle, setSearchTitle] = useState(undefined);
  const [searchFolder, setSearchFolder] = useState(undefined);
  const pageSize = 10;

  const broadcasts = useSelector(selectBroadcastsSortedByUpdatedAt);
  const isLoadingBroadcasts = useSelector(selectIsLoadingBroadcasts);
  const isLoadingFolders = useSelector(selectIsLoadingFolders);
  const folders = useSelector(selectFolders);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchBroadcasts({ page, pageSize, searchTitle, searchFolder }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize, searchTitle, searchFolder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const dismissError = () => setError(undefined);

  const mapBroadcastToTableRow = (broadcast) => (
    <tr key={`broadcast-${broadcast.id}`}>
      <td>
        <Link to={`/arrivideo/broadcast/edit/${broadcast.id}`}>
          {broadcast.title}
        </Link>
      </td>
      <td>{moment(broadcast.from).format("LLL")}</td>
      <td>{moment(broadcast.to).format("LLL")}</td>
      <td>{broadcast.actionType}</td>
      <td>{broadcast.folder.title || intl.formatMessage(messages.noFolder)}</td>
      <td>{moment(broadcast.updatedAt).format("LLL")}</td>
    </tr>
  );

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {intl.formatMessage(genericTranslations.error)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      <SearchBroadcastForm
        folders={folders}
        searchTitle={searchTitle}
        setSearchTitle={setSearchTitle}
        setSearchFolder={setSearchFolder}
        messages={messages}
      />

      {(isLoadingBroadcasts || isLoadingFolders) && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {broadcasts.length === 0 && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {broadcasts.length > 0 && !isLoadingBroadcasts && !isLoadingFolders && (
        <Table responsive>
          <thead>
            <tr>
              <th>{intl.formatMessage(genericTranslations.title)}</th>
              <th>{intl.formatMessage(genericTranslations.from)}</th>
              <th>{intl.formatMessage(messages.to)}</th>
              <th>{intl.formatMessage(messages.action)}</th>
              <th>{intl.formatMessage(messages.folder)}</th>
              <th>{intl.formatMessage(genericTranslations.updatedAt)}</th>
            </tr>
          </thead>
          <tbody>{broadcasts.map(mapBroadcastToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter
        actionPath="/arrivideo/broadcast/add"
        onSelectPage={onSelectPage}
        page={page}
        pages={pages}
      />
    </div>
  );
}

export default Broadcasts;
